'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Alert,
  Button,
  Divider,
  Container,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  Drawer,
  Fab
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import AddIcon from '@mui/icons-material/Add';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
// import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import ChatArea from '../components/playground/ChatArea';
import MessageInput from '../components/playground/MessageInput';
import BookmarkedMessagesPanel from '../components/playground/BookmarkedMessagesPanel';
import { playgroundStyles } from '../styles/playground';
import Navbar from '@/components/home/Navbar';
interface ConversationMessage {
  role: 'user' | 'assistant' | 'error';
  content: string;
  timestamp?: string;
  is_error?: boolean;
}

interface Conversations {
  [modelId: string]: ConversationMessage[];
}

interface Model {
  id: string;
  name: string;
  provider: string;
  description?: string;
}

interface Conversation {
  id: string;
  project_id: string;
  user_id: string;
  title: string;
  messages: string;
  latest_run_id?: string;
  created_at: string;
  updated_at: string;
  message_count?: number;
}

interface WebSocketMessage {
  id: string;
  content: string;
  sender: 'user' | 'bot';
  type: 'message' | 'response' | 'error' | 'system';
  timestamp: string;
  conversation_id?: string;
  metadata?: {
    timeoutId?: NodeJS.Timeout | null;
    [key: string]: any;
  };
}

export default function PlaygroundPage() {
  const theme = useTheme();
  const styles = playgroundStyles(theme);
  // const { t } = useTranslation();
  const { projectId: projectIdStr } = useParams<{ projectId: string }>();
  const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
  const { user } = useAuth();

  // WebSocket状态
  const {
    isConnected,
    connectionStatus,
    messages: wsMessages,
    error,
    sendMessage,
    clearMessages,
    connect,
    disconnect,
    onMessage,
    onStatusChange,
    subscribeProject,
    onTaskProgress
  } = useWebSocket();

  // 报告生成状态
  const [reportStatus, setReportStatus] = useState<{
    report_generation_status: string;
    report_generation_progress: number;
    report_generation_error: string | null;
  } | null>(null);
  const [statusChecking, setStatusChecking] = useState(false);

  // 本地状态
  const [loading, setLoading] = useState<boolean>(false);
  const [userInput, setUserInput] = useState('');

  // 标记消息状态
  const [bookmarkedMessages, setBookmarkedMessages] = useState<Set<string>>(new Set());
  const [bookmarkedMessageDetails, setBookmarkedMessageDetails] = useState<Map<string, any>>(new Map());
  const [isBookmarksPanelOpen, setIsBookmarksPanelOpen] = useState<boolean>(true);

  // AI工作流进度状态
  const [workflowProgress, setWorkflowProgress] = useState<{
    stepId: string;
    stepName: string;
    progress: number;
    message: string;
    status: 'preparing' | 'processing' | 'completed' | 'error';
  } | null>(null);

  // 检查报告生成状态
  const checkReportStatus = useCallback(async () => {
    if (!projectId) return;

    setStatusChecking(true);
    try {
      const response = await apiClient.get(`/projects/${projectId}/report-status`);
      if (response && response.success) {
        setReportStatus(response.data);
      }
    } catch (error) {
      console.error('获取报告状态失败:', error);
    } finally {
      setStatusChecking(false);
    }
  }, [projectId]);

  // AI探索页面不再展示报告状态信息，移除定时检查
  // useEffect(() => {
  //   if (projectId) {
  //     checkReportStatus();
  //     const interval = setInterval(checkReportStatus, 5000); // 每5秒检查一次
  //     return () => clearInterval(interval);
  //   }
  // }, [projectId, checkReportStatus]);

  // 会话历史相关状态
  const [conversationHistory, setConversationHistory] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messages, setMessages] = useState<WebSocketMessage[]>([]);

  
  // 获取历史会话记录
  const loadConversations = useCallback(async () => {
    if (!user || !projectId) return;

    setIsLoadingHistory(true);
    try {
      const response = await apiClient.conversations.getList(projectId.toString(), user.id.toString());
      // The API returns the conversation list directly, not wrapped in a response object
      if (Array.isArray(response)) {
        const conversationList = response;
        setConversationHistory(conversationList);

        // 自动选择最新的会话
        if (conversationList.length > 0 && !selectedConversation) {
          const latestConversation = conversationList[0];
          setSelectedConversation(latestConversation);
          await loadConversationMessages(latestConversation);
        }
      }
    } catch (error) {
      console.error('获取会话列表失败:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user, projectId, selectedConversation]);

  // 加载会话消息
  const loadConversationMessages = useCallback(async (conversation: Conversation) => {
    if (!projectId) return;

    try {
      // 使用detail端点获取包含消息详情的对话数据
      const response = await apiClient.conversations.getById(projectId.toString(), conversation.id);

      // The API returns the conversation data directly, not wrapped in a response object
      if (response && (response.message_list || response.messages)) {
        const conversationData = response;

        // 优先使用messages字段（现在包含ChatMessage对象列表），如果没有则回退到message_list字段
        let messagesData: any[] = [];

        if (conversationData.messages && Array.isArray(conversationData.messages)) {
          // 使用新的messages字段（现在是ChatMessage对象列表）
          messagesData = conversationData.messages.map((msg: any) => ({
            id: msg.id, // 保留数据库主键ID
            role: msg.role,
            content: msg.content,
            type: msg.message_type,
            metadata: msg.message_metadata || {},
            timestamp: msg.timestamp || msg.created_at
          }));
        } else if (conversationData.message_list && Array.isArray(conversationData.message_list)) {
          // 使用message_list字段（来自ChatMessages表）
          messagesData = conversationData.message_list;
        } else if (conversationData.messages && typeof conversationData.messages === 'string') {
          // 回退到传统的JSON字符串字段
          messagesData = JSON.parse(conversationData.messages || '[]');
        }

        // 将历史消息转换为WebSocket消息格式
        const historyMessages: WebSocketMessage[] = messagesData.map((msg: any, index: number) => ({
          id: msg.id ? msg.id.toString() : `${conversation.id}-${index}`, // 优先使用数据库ID
          content: msg.content,
          sender: msg.role === 'user' ? 'user' : 'bot',
          type: msg.role === 'error' ? 'error' : 'message',
          timestamp: msg.timestamp || msg.created_at || new Date().toISOString(),
          conversation_id: conversation.id
        }));

        setMessages(historyMessages);
      } else {
        // 如果没有消息，清空消息列表
        setMessages([]);
      }
    } catch (error) {
      console.error('加载会话消息失败:', error);
    }
  }, [projectId]);

  // 创建新会话
  const createNewConversation = useCallback(async () => {
    if (!user || !projectId) return;

    try {
      const newTitle = `新会话 ${new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
      const response = await apiClient.conversations.create(projectId.toString(), user.id.toString(), newTitle);

      // The API returns the conversation data directly, not wrapped in a response object
      if (response && response.id) {
        const newConversation = response;
        setConversationHistory(prev => [newConversation, ...prev]);
        setSelectedConversation(newConversation);
        setMessages([]);
        setDrawerOpen(false);
      }
    } catch (error) {
      console.error('创建会话失败:', error);
    }
  }, [user, projectId]);

  // 选择会话
  const handleSelectConversation = useCallback(async (conversation: Conversation) => {
    setSelectedConversation(conversation);
    await loadConversationMessages(conversation);
  }, [loadConversationMessages]);

  // 删除会话
  const handleDeleteConversation = useCallback(async (conversation: Conversation, event: React.MouseEvent) => {
    event.stopPropagation(); // 阻止事件冒泡，避免触发选择会话

    if (!projectId) return;

    try {
      await apiClient.conversations.delete(projectId.toString(), conversation.id);

      // 从会话列表中移除
      setConversationHistory(prev => prev.filter(c => c.id !== conversation.id));

      // 如果删除的是当前选中的会话，清空选择和消息
      if (selectedConversation?.id === conversation.id) {
        setSelectedConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('删除会话失败:', error);
    }
  }, [projectId, selectedConversation]);

  // 加载用户的标记消息
  const loadBookmarkedMessages = useCallback(async () => {
    if (!user || !projectId) return;

    try {
      const response = await apiClient.bookmarks.getList({
        project_id: projectId,
        limit: 1000 // 获取最多1000条标记消息
      });

      console.log('🔖 加载标记消息响应:', response);

      if (response && response.bookmarks) {
        const bookmarkSet = new Set<string>();
        const bookmarkDetailsMap = new Map<string, any>();

        response.bookmarks.forEach((bookmark: any) => {
          const messageId = bookmark.message_id.toString(); // 转换为字符串以匹配消息ID
          console.log('🔖 处理标记消息:', { messageId, bookmark });
          bookmarkSet.add(messageId);
          bookmarkDetailsMap.set(messageId, {
            id: bookmark.id,
            content: bookmark.content,
            timestamp: bookmark.timestamp,
            role: bookmark.role,
            bookmarkedAt: bookmark.bookmarked_at
          });
        });

        console.log('🔖 设置标记消息状态:', bookmarkSet);
        setBookmarkedMessages(bookmarkSet);
        setBookmarkedMessageDetails(bookmarkDetailsMap);
      }
    } catch (error) {
      console.error('加载标记消息失败:', error);
    }
  }, [user, projectId]);

  // 初始化加载会话
  useEffect(() => {
    if (user && projectId) {
      loadConversations();
      loadBookmarkedMessages();
    }
  }, [user, projectId, loadConversations, loadBookmarkedMessages]);

  // 处理WebSocket消息
  useEffect(() => {
    const unsubscribeMessage = onMessage((message) => {
      // 调试信息：记录收到的消息类型
      console.log('收到WebSocket消息:', {
        type: message.type,
        hasContent: !!message.content,
        contentLength: message.content?.length || 0,
        sender: message.type === 'error' ? 'system' : 'bot'
      });

      // 处理所有类型的响应消息
      if (message.type === 'message.completed' || message.type === 'response' || message.type === 'error') {
        // 停止加载状态
        setLoading(false);

        // 清除所有待处理的超时定时器
        setMessages(prev => {
          const updatedMessages = prev.map(msg => {
            if (msg.metadata?.timeoutId) {
              clearTimeout(msg.metadata.timeoutId);
              return { ...msg, metadata: { ...msg.metadata, timeoutId: null } };
            }
            return msg;
          });
          return updatedMessages;
        });

        // 将WebSocket消息添加到消息列表中
        if (message.content && message.content.trim()) {
          const newMessage: WebSocketMessage = {
            id: message.message_id || `ws-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`, // 优先使用后端返回的真实消息ID
            content: message.content.trim(),
            sender: message.type === 'error' ? 'system' : 'bot',
            type: message.type === 'error' ? 'error' : 'message',
            timestamp: new Date().toISOString(),
            conversation_id: selectedConversation?.id
          };

          setMessages(prev => {
            // 检查是否已存在相同内容的消息，避免重复
            const exists = prev.some(msg =>
              msg.content === newMessage.content &&
              msg.sender === newMessage.sender &&
              Math.abs(new Date(msg.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 5000
            );

            if (exists) {
              return prev;
            }

            return [...prev, newMessage];
          });
        }

        // 如果收到响应或完成消息，清除进度显示
        setTimeout(() => {
          setWorkflowProgress(null);
        }, 1000);
      }

      // 处理工作流进度更新
      if (message.type === 'step.progress' && message.data) {
        const progressData = message.data;
        setWorkflowProgress({
          stepId: progressData.step_id,
          stepName: progressData.step_name,
          progress: progressData.progress,
          message: progressData.message,
          status: progressData.status
        });

        // 如果步骤完成，清除进度显示
        if (progressData.status === 'completed') {
          setTimeout(() => {
            setWorkflowProgress(null);
          }, 2000);
        }
      }

      // 处理处理开始消息
      if (message.type === 'processing.started') {
        setWorkflowProgress({
          stepId: 'processing',
          stepName: 'AI处理',
          progress: 10,
          message: message.content || '正在处理您的请求...',
          status: 'preparing'
        });
      }
    });

    return () => {
      if (typeof unsubscribeMessage === 'function') {
        unsubscribeMessage();
      }
    };
  }, [onMessage, selectedConversation]);

  // 处理连接状态变化
  useEffect(() => {
    const unsubscribeStatus = onStatusChange((status) => {
      console.log('连接状态变化:', status);
    });

    return () => {
      if (typeof unsubscribeStatus === 'function') {
        unsubscribeStatus();
      }
    };
  }, [onStatusChange]);

  // 订阅项目WebSocket更新
  useEffect(() => {
    if (isConnected && projectId) {
      // 订阅项目更新
      subscribeProject(parseInt(projectId));
      console.log(`已订阅项目 ${projectId} 的WebSocket更新`);
    }
  }, [isConnected, projectId, subscribeProject]);

  // 处理任务进度更新
  useEffect(() => {
    const unsubscribeTaskProgress = onTaskProgress((data) => {
      console.log('收到任务进度更新:', data);

      // AI探索页面不再展示报告状态，不再需要刷新
      // if (data && data.project_id && parseInt(data.project_id) === parseInt(projectId)) {
      //   console.log('当前项目任务进度更新，刷新状态');
      //   checkReportStatus();
      // }
    });

    return () => {
      if (typeof unsubscribeTaskProgress === 'function') {
        unsubscribeTaskProgress();
      }
    };
  }, [onTaskProgress, projectId, checkReportStatus]);

  // 标记消息管理函数
  const handleToggleBookmark = useCallback(async (messageId: string) => {
    console.log('🚀🚀🚀 handleToggleBookmark 函数被调用了！messageId:', messageId);
    try {
      console.log('🔖 标记消息 - messageId:', messageId);
      console.log('🔖 当前消息列表:', messages.map(m => ({ id: m.id, sender: m.sender, content: m.content.substring(0, 50) + '...' })));

      // 检查当前是否已标记
      const isCurrentlyBookmarked = bookmarkedMessages.has(messageId);
      console.log('🔖 是否已标记:', isCurrentlyBookmarked);

      if (isCurrentlyBookmarked) {
        // 取消标记
        await apiClient.bookmarks.deleteByMessageId(messageId);

        setBookmarkedMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });

        // 从详情中移除
        setBookmarkedMessageDetails(details => {
          const newDetails = new Map(details);
          newDetails.delete(messageId);
          return newDetails;
        });
      } else {
        // 添加标记 - 查找对应的消息
        console.log('🔖 开始查找消息，messageId:', messageId);
        console.log('🔖 当前消息列表:', messages.map(m => ({
            id: m.id,
            id_type: typeof m.id,
            content: m.content.substring(0, 30) + '...'
        })));
        console.log('🔖 selectedConversation:', selectedConversation);

        console.log('🔖 查找匹配，messageId:', messageId, typeof messageId);

        // 确保类型一致性：将 messageId 和 msg.id 都转换为字符串进行比较
        const messageIdStr = messageId.toString();

        const message = messages.find(msg => {
            const msgIdStr = msg.id.toString();
            console.log('🔍 比较消息:', {
                msg_id: msg.id,
                msg_id_str: msgIdStr,
                msg_id_type: typeof msg.id,
                target_id: messageId,
                target_id_str: messageIdStr,
                target_type: typeof messageId,
                equal: msgIdStr === messageIdStr
            });
            return msgIdStr === messageIdStr; // 使用严格比较 ===
        });

        console.log('🔖 找到的消息:', message);
        console.log('🔖 selectedConversation 存在:', !!selectedConversation);

        if (message && selectedConversation) {
          console.log('🔖 准备调用 API');
          console.log('🔖 messageId (原始):', messageId, typeof messageId);
          console.log('🔖 message 内容:', message);
          console.log('🔖 selectedConversation:', selectedConversation);

          const numericMessageId = parseInt(messageId);
          console.log('🔖 转换后的数字ID:', numericMessageId, typeof numericMessageId);

          // 检查转换结果是否有效
          if (isNaN(numericMessageId)) {
            console.error('❌ messageId 转换失败:', messageId);
            return;
          }

          console.log('🔖 即将调用 apiClient.bookmarks.create');

          try {
            console.log('🔖 开始调用 API...');
            await apiClient.bookmarks.create({
              message_id: numericMessageId, // 转换为数字ID
              content: message.content,
              role: message.sender === 'bot' ? 'assistant' : message.sender,
              timestamp: message.timestamp || new Date().toISOString(),
              conversation_id: parseInt(selectedConversation.id)
            });
            console.log('✅ API 调用成功');
          } catch (apiError) {
            console.error('❌ API 调用失败:', apiError);
            throw apiError;
          }

          setBookmarkedMessages(prev => {
            const newSet = new Set(prev);
            newSet.add(messageId);
            return newSet;
          });

          // 添加到详情中
          setBookmarkedMessageDetails(details => {
            const newDetails = new Map(details);
            newDetails.set(messageId, {
              id: messageId,
              content: message.content,
              timestamp: message.timestamp,
              role: 'assistant'
            });
            return newDetails;
          });
        }
      }
      console.log('🔖 标记操作完成');
    } catch (error) {
      console.error('🔖 标记操作失败:', error);
      // 可以在这里添加用户提示
    }
  }, [messages, bookmarkedMessages, selectedConversation]);

  const handleJumpToMessage = useCallback((messageId: string) => {
    // 滚动到指定消息
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 高亮效果
      messageElement.style.backgroundColor = 'rgba(25, 118, 210, 0.1)';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  }, []);

  // 发送消息
  const handleSendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // 检查是否正在加载中，如果是则禁止发送
    if (loading) {
      console.log('AI正在回复中，请等待完成后再发送新消息');
      return;
    }

    // 如果没有选择会话，自动创建一个新会话
    let currentConversation = selectedConversation;
    if (!currentConversation && user && projectId) {
      try {
        const newTitle = `新会话 ${new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
        const response = await apiClient.conversations.create(projectId.toString(), user.id.toString(), newTitle);

        if (response && response.id) {
          const newConversation = response;
          setConversationHistory(prev => [newConversation, ...prev]);
          setSelectedConversation(newConversation);
          currentConversation = newConversation;
        }
      } catch (error) {
        console.error('自动创建会话失败:', error);
        return;
      }
    }

    // 开始加载
    setLoading(true);

    // 添加用户消息到消息列表
    const userMessage: WebSocketMessage = {
      id: `user-${Date.now()}`,
      content: content,
      sender: 'user',
      type: 'message',
      timestamp: new Date().toISOString(),
      conversation_id: currentConversation?.id
    };

    // 设置超时保护，如果180秒后没有收到回复，自动重置loading状态
    const timeoutId = setTimeout(() => {
      setLoading(false);
      console.warn('AI回复超时，已自动重置状态');
    }, 180000);

    // 一次性添加用户消息和timeout ID
    setMessages(prev => [...prev, { ...userMessage, metadata: { timeoutId } } as any]);

    // 通过WebSocket发送
    sendMessage(content, {
      project_id: projectId?.toString(),
      conversation_id: currentConversation?.id
    });

    // 清空输入
    setUserInput('');
  }, [loading, sendMessage, projectId, selectedConversation, user]);

  // 处理输入变化
  const handleMessageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(event.target.value);
  }, []);

  // 处理键盘事件
  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage(userInput);
    }
  }, [handleSendMessage, userInput]);

  
  // 重新连接
  const handleReconnect = useCallback(() => {
    disconnect();
    setTimeout(() => connect(), 1000);
  }, [connect, disconnect]);

  
  // 获取连接状态
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'connected': return 'success';
      case 'connecting': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  }, []);

  const getStatusText = useCallback((status: string) => {
    switch (status) {
      case 'connected': return '已连接';
      case 'connecting': return '连接中';
      case 'error': return '连接错误';
      default: return '未连接';
    }
  }, []);

  
  // 将messages转换为conversations格式
  const conversations: Conversations = useMemo(() => {
    const result: Conversations = {};
    const modelId = 'default';

    // 过滤掉空内容消息
    const validMessages = messages.filter(msg => msg.content && msg.content.trim());

    // 如果没有消息，显示欢迎消息
    if (validMessages.length === 0) {
      result[modelId] = [
        { role: 'assistant', content: '你好！我是AI智能助手，可以帮助你进行绩效分析、指标设计和报告生成。有什么我可以帮助你的吗？' }
      ];
    } else {
      // 转换消息格式，确保去除空内容的消息，保留数据库ID
      result[modelId] = validMessages.map(msg => ({
        id: msg.id, // 保留数据库主键ID
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp,
        is_error: msg.type === 'error'
      }));
    }

    return result;
  }, [messages]);

  return (
    <Box>
      <Navbar />
      <Container maxWidth={false} sx={{ mt: 3, mb: 4, px: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          AI对话
        </Typography>

        {/* 主要布局：左侧会话列表 + 右侧聊天区域 */}
        <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)', gap: 2 }}>

          {/* 左侧会话列表 */}
          <Paper sx={{ width: 300, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyIcon fontSize="small" />
                  AI助手
                </Typography>

                <Button
                  variant="contained"
                  onClick={createNewConversation}
                  size="small"
                  sx={{
                    minWidth: '32px',
                    maxWidth: '32px',
                    height: '32px',
                    p: 0,
                    borderRadius: '50%'
                  }}
                >
                  <AddIcon />
                </Button>
              </Box>
            </Box>

            <Divider />

            {isLoadingHistory ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List sx={{ flex: 1, overflow: 'auto' }}>
                {conversationHistory.map((conversation) => (
                  <ListItem key={conversation.id} disablePadding sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', p: 1 }}>
                      <ListItemButton
                        selected={selectedConversation?.id === conversation.id}
                        onClick={() => handleSelectConversation(conversation)}
                        sx={{
                          flex: 1,
                          '&.Mui-selected': {
                            backgroundColor: 'action.selected',
                            borderLeft: 3,
                            borderColor: 'primary.main'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ChatIcon fontSize="small" />
                        </ListItemIcon>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, mb: 0.5 }}>
                            {conversation.title}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(conversation.updated_at).toLocaleString('zh-CN', {
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>
                            {conversation.message_count && (
                              <Chip
                                label={`${conversation.message_count} 条消息`}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: '20px',
                                  fontSize: '0.75rem',
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </ListItemButton>
                      {selectedConversation?.id !== conversation.id && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteConversation(conversation, e)}
                          sx={{
                            ml: 1,
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'error.main'
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>

          {/* 中间聊天区域 */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* 头部控制区域 */}
            <Paper sx={{ p: 2, mb: 2 }}>
              {/* 连接状态 */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  {selectedConversation ? selectedConversation.title : '新对话'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Chip
                    label={`WebSocket: ${getStatusText(connectionStatus)}`}
                    color={getStatusColor(connectionStatus)}
                    size="small"
                  />
                  {!isConnected && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ cursor: 'pointer' }}
                      onClick={handleReconnect}
                    >
                      重新连接
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* 报告生成状态 */}
              {/* {reportStatus && (
                <Alert
                  severity={reportStatus.report_generation_status === 'generating' ? 'info' :
                           reportStatus.report_generation_status === 'completed' ? 'success' :
                           reportStatus.report_generation_error ? 'error' : 'warning'}
                  sx={{ mt: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        报告生成状态: {reportStatus.report_generation_status === 'generating' ? '生成中' :
                                      reportStatus.report_generation_status === 'completed' ? '已完成' :
                                      reportStatus.report_generation_status === 'failed' ? '失败' : '等待中'}
                      </Typography>
                      {reportStatus.report_generation_status === 'generating' && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          进度: {reportStatus.report_generation_progress || 0}%
                        </Typography>
                      )}
                      {reportStatus.report_generation_error && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          错误: {reportStatus.report_generation_error}
                        </Typography>
                      )}
                    </Box>
                    {statusChecking && (
                      <CircularProgress size={16} sx={{ ml: 2 }} />
                    )}
                  </Box>
                </Alert>
              )} */}

              {error && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => { }}>
                  {error}
                </Alert>
              )}
            </Paper>


            {/* 聊天区域 */}
            <Box sx={{ flex: 1, overflow: 'hidden', mb: 2 }}>
              <ChatArea
                selectedModels={['default']}
                conversations={conversations}
                loading={loading}
                getModelName={() => 'AI助手'}
                onClearConversation={() => {}}
                bookmarkedMessages={bookmarkedMessages}
                onToggleBookmark={handleToggleBookmark}
              />
            </Box>

            {/* 消息输入区域 */}
            <Paper sx={{ p: 2 }}>
              <MessageInput
                userInput={userInput}
                handleInputChange={handleMessageChange}
                handleSendMessage={async () => await handleSendMessage(userInput)}
                handleKeyPress={handleKeyPress}
                loading={{ 'default': loading }}
                selectedModels={['default']}
                isConnected={isConnected}
                projectId={projectId ? parseInt(projectId) : undefined}
              />
            </Paper>
          </Box>

          {/* 右侧标记消息面板 */}
          <BookmarkedMessagesPanel
            bookmarkedMessages={bookmarkedMessageDetails}
            onRemoveBookmark={async (messageId) => {
              try {
                await apiClient.bookmarks.deleteByMessageId(messageId);

                setBookmarkedMessages(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(messageId);
                  return newSet;
                });
                setBookmarkedMessageDetails(details => {
                  const newDetails = new Map(details);
                  newDetails.delete(messageId);
                  return newDetails;
                });
              } catch (error) {
                console.error('删除标记失败:', error);
              }
            }}
            onJumpToMessage={handleJumpToMessage}
            isOpen={isBookmarksPanelOpen}
            onToggle={() => setIsBookmarksPanelOpen(!isBookmarksPanelOpen)}
          />
        </Box>
      </Container>
    </Box>
  );
}